import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { useRedirectToPage } from "src/hooks";
import DashboardLayout from "src/layouts/DashboardLayout";
import AlertMessageContainer from "./AlertMessageContainer";
import ProgressListContainer from "./ProgressListContainer";
import { Button, Spinner, UserAddIcon } from "@porchpass/ui";
import { UserContext, useAuth } from "src/context";
import { dealerStorage } from "src/utils";
import type {
  DomainBorrowerStatus,
  DtoApplicationProgressResp,
  DtoBorrowerProgress,
  DtoCoBorrowerProgress,
} from "@porchpass/api-client";
import AddCoborrowerModal from "./AddCoborrowerModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import RemoveCoborrowerModal from "./RemoveCoborrowerModal";
import ReopenApplicationModal from "./ReopenApplicationModal";
import { ProgressInitializer } from "src/initializers";

const MAX_RETRIES = 100;
const RETRY_DELAY = 10 * 1000;

const getCoborrowerDataForModalProps = (
  coborrowers?: DtoCoBorrowerProgress[],
) => {
  if (coborrowers && coborrowers[0]) {
    const {
      first_name,
      last_name,
      borrower_id: coborrower_id,
    } = coborrowers[0];
    return {
      coborrower_id,
      coborrowerName: `${first_name} ${last_name}`,
    };
  }
  return {
    coborrower_id: "",
    coborrowerName: "",
  };
};

type ProgressPageContentProps = {
  formData: Partial<DtoApplicationProgressResp>;
  isLoading: boolean;
  refreshProgress: () => Promise<void>;
};

const ProgressPageContent = ({
  formData,
  isLoading,
  refreshProgress,
}: ProgressPageContentProps) => {
  const { user } = useAuth();
  const { userData } = useContext(UserContext);
  const { dealerName } = useParams();
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const [searchParams] = useSearchParams();

  const isReturnedFromDocusign = searchParams.get("event") ?? "";

  const navigateToDashboard = () => navigate(`/${dealerName}/dashboard`);
  const navigateToRegisterPage = useRedirectToPage("/../../../../register");
  const navigateToInvitePage = useRedirectToPage("../../coborrower-invite");
  const [isAddCoborrowerModalOpen, setIsAddCoborrowerModalOpen] =
    useState(false);
  const addCoborrowerModalCloseHandler = () =>
    setIsAddCoborrowerModalOpen(false);
  const addCoborrowerModalConfirmHandler = (isOnSameDevice: boolean) => {
    if (isOnSameDevice) {
      dealerStorage.removeItem("email-for-register");
      dealerStorage.removeItem("phone-for-register");
      navigateToRegisterPage();
    } else {
      navigateToInvitePage();
    }
  };
  const addCoborrowerClickHandler = () => {
    if (application_status !== "STARTED") {
      setIsReopenApplicationModalOpen(true);
    } else {
      setIsAddCoborrowerModalOpen(true);
    }
  };

  const [isRemoveCoborrowerModalOpen, setIsRemoveCoborrowerModalOpen] =
    useState(false);
  const removeCoborrowerModalCloseHandler = () =>
    setIsRemoveCoborrowerModalOpen(false);
  const removeCoborrowerModalConfirmHandler = async () => {
    if (coborrower_id === userData?.auth_user_borrower_id) {
      navigate(`/${dealerName}/logout`);
      return;
    }

    await refreshProgress();
    setIsRemoveCoborrowerModalOpen(false);
  };
  const removeCoborrowerClickHandler = () => {
    if (application_status !== "STARTED") {
      setIsReopenApplicationModalOpen(true);
    } else {
      setIsRemoveCoborrowerModalOpen(true);
    }
  };
  const { coborrower_id, coborrowerName } = getCoborrowerDataForModalProps(
    formData.co_borrowers,
  );

  const [isReopenApplicationModalOpen, setIsReopenApplicationModalOpen] =
    useState(false);
  const reopenApplicationModelCloseHandler = () =>
    setIsReopenApplicationModalOpen(false);
  const reopenApplicationModelConfirmHandler = async () => {
    if (editSectionNavigationAfterReopen !== undefined) {
      editSectionNavigationAfterReopen();
    } else {
      await refreshProgress();
      setIsReopenApplicationModalOpen(false);
    }
  };

  const hasSignedStatusType = (status?: DomainBorrowerStatus) => {
    const submitStatuses: DomainBorrowerStatus[] = [
      "READY_TO_SIGN_SUBMIT",
      "SIGNED",
      "WAITING_ON_ESIGNATURE_SERVICE",
      "DECLINED_TO_SIGN",
    ];
    return status && submitStatuses.includes(status);
  };

  const allBorrowersReadyToSubmit = () => {
    if (!hasSignedStatusType(formData.borrower?.status)) return false;

    return !!formData.co_borrowers?.every((co_borrower) =>
      hasSignedStatusType(co_borrower.status),
    );
  };

  const [
    editSectionNavigationAfterReopen,
    setEditSectionNavigationAfterReopen,
  ] = useState<() => void>();
  const editSectionClickHandler = (openLink: () => void) => {
    setEditSectionNavigationAfterReopen((_) => () => openLink());
    setIsReopenApplicationModalOpen(true);
  };

  const { application_id, application_status, borrower, co_borrowers } =
    formData;

  useEffect(() => {
    if (!formData || !isReturnedFromDocusign || retryCount > MAX_RETRIES) {
      return;
    }

    const isApplicationSubmitted =
      application_status === "SUBMITTED" || application_status === "REVIEWED";
    const hasBorrowerSigned = borrower?.status === "SIGNED";
    const hasCoborrowerSigned = co_borrowers?.every(
      (x) => x.status === "SIGNED",
    );

    const hasAllBorrowerSigned = hasBorrowerSigned && hasCoborrowerSigned;

    if (!hasAllBorrowerSigned || !isApplicationSubmitted) {
      const timer = setTimeout(() => {
        void refreshProgress().then(() => setRetryCount(retryCount + 1));
      }, RETRY_DELAY);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  if (!user) return null;

  const isWaitingOnEsignatureService = Boolean(
    application_status === "WAITING_ON_ESIGNATURE_SERVICE" ||
      borrower?.status === "WAITING_ON_ESIGNATURE_SERVICE" ||
      co_borrowers?.find(
        (cobo) => cobo.status === "WAITING_ON_ESIGNATURE_SERVICE",
      ),
  );
  const shouldDisableActions =
    isWaitingOnEsignatureService || application_status === "REVIEWED";
  const isAppSignAndSubmitReady = allBorrowersReadyToSubmit();

  return (
    <DashboardLayout pageTitle="Progress">
      {isLoading ? (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 p-5">
          <Spinner variant="blue" size="l" />
        </div>
      ) : (
        <div className="flex h-full w-full flex-col items-center gap-4 p-8">
          <h2
            className="mb-5 text-center text-2xl font-semibold"
            data-replay-safe
          >
            Application #{application_id}
          </h2>
          <AlertMessageContainer formData={formData} />
          {formData.borrower && (
            <ProgressListContainer<DtoBorrowerProgress>
              key={`${formData.borrower.first_name}-${formData.borrower.last_name}`}
              borrower={formData.borrower}
              applicationId={formData.application_id ?? ""}
              applicationStatus={formData.application_status}
              isWaitingOnEsignatureService={isWaitingOnEsignatureService}
              editSectionClickHandler={editSectionClickHandler}
              isAppSignAndSubmitReady={isAppSignAndSubmitReady}
            />
          )}
          {formData.co_borrowers?.length ? (
            formData.co_borrowers.map((coBorrower) => (
              <ProgressListContainer<DtoCoBorrowerProgress>
                key={`${coBorrower.first_name}-${coBorrower.last_name}`}
                borrower={coBorrower}
                isCoborrower={true}
                applicationId={formData.application_id ?? ""}
                applicationStatus={formData.application_status}
                isWaitingOnEsignatureService={isWaitingOnEsignatureService}
                removeCoborrowerClickHandler={removeCoborrowerClickHandler}
                editSectionClickHandler={editSectionClickHandler}
                isAppSignAndSubmitReady={isAppSignAndSubmitReady}
              />
            ))
          ) : (
            <Button
              type="button"
              variant="ghost-blue"
              className="w-full justify-center rounded-2xl p-4 text-base disabled:opacity-25"
              onClick={addCoborrowerClickHandler}
              disabled={shouldDisableActions}
              data-replay-safe
            >
              <UserAddIcon size={16} />
              Add a co-borrower
            </Button>
          )}
          {(formData?.application_status === "SUBMITTED" ||
            formData?.application_status === "REVIEWED") && (
            <>
              <br />
              <Button
                type="button"
                variant="primary"
                size="l"
                className="mx-4 my-4 w-full max-w-full"
                onClick={navigateToDashboard}
              >
                Go to Dashboard
              </Button>
            </>
          )}
        </div>
      )}
      <AddCoborrowerModal
        isOpen={isAddCoborrowerModalOpen}
        closeHandler={addCoborrowerModalCloseHandler}
        confirmHandler={addCoborrowerModalConfirmHandler}
      />
      <RemoveCoborrowerModal
        isOpen={isRemoveCoborrowerModalOpen}
        closeHandler={removeCoborrowerModalCloseHandler}
        confirmHandler={() => void removeCoborrowerModalConfirmHandler()}
        coborrowerName={coborrowerName}
        coborrower_id={coborrower_id ?? ""}
      />
      <ReopenApplicationModal
        isOpen={isReopenApplicationModalOpen}
        closeHandler={reopenApplicationModelCloseHandler}
        confirmHandler={() => void reopenApplicationModelConfirmHandler()}
      />
    </DashboardLayout>
  );
};

export default () => (
  <ProgressInitializer>
    {(props) => <ProgressPageContent {...props} />}
  </ProgressInitializer>
);
